import { Controller } from "@hotwired/stimulus"
import I18n from "../../i18n"

const t = (key, options) => I18n.t(`erp.sales.js.marking_form.${key}`, options)

// One marking row: which textile lines it applies to, and the tariff lookup that follows
// from the resulting total quantity.
// Connects to data-controller="erp--marking-form"
export default class extends Controller {
  static values = {
    lookupUrl: String,
    dimensionsUrl: String,
    customerType: String,
    structuredPricing: Boolean
  }

  static targets = [
    "technique", "canonicalTechnique", "subTechnique", "position", "canonicalZone", "customZone",
    "dimension", "width", "height", "textileType", "compatibilityOverride", "compatibilityReason", "manualPriceReason",
    "quantity", "unitPrice", "priceOverride", "priceFeedback", "pricing",
    "linkRow", "linkCheckbox", "linkQuantity", "linkDestroy",
    "allocationRow", "allocationCheckbox", "allocationQuantity", "allocationDestroy", "allocationCount",
    "compatibilityRow", "extraRow", "extraCheckbox", "extraDestroy", "extraEmpty",
    "techniqueBadge", "positionBadge", "linkSummary", "quantitySummary",
    "unitPriceSummary", "discountSummary", "totalSummary"
  ]

  connect() {
    this._dimensionsAbortController = null
    this.compatibilityOverrideChanged()
    this.#toggleManualPriceReason()
    this.#syncExtraAvailability()
    this.#refreshCompatibility()
    this.#syncQuantity()
    if (this.hasTechniqueTarget && this.techniqueTarget.value) {
      if (this.structuredPricingValue) this.loadDimensions()
      else this.loadDimensions().then(() => this.lookupPrice())
    }
  }

  disconnect() {
    this._dimensionsAbortController?.abort()
  }

  // ── Attachments to textile lines ─────────────────────────────

  toggleLink(event) {
    const row = event.target.closest("[data-erp--marking-form-target='linkRow']")
    if (!row) return

    const destroy = row.querySelector("[data-erp--marking-form-target='linkDestroy']")
    if (destroy) destroy.value = event.target.checked ? "0" : "1"

    this.#syncQuantity()
    this.lookupPrice()
    this.#notifyFormMutation(destroy)
  }

  selectAll(event) {
    event.preventDefault()

    this.linkCheckboxTargets.forEach(checkbox => {
      if (checkbox.checked) return

      checkbox.checked = true
      checkbox.dispatchEvent(new Event("change", { bubbles: true }))
    })
  }

  quantityChanged() {
    this.#syncQuantity()
    this.lookupPrice()
  }

  toggleAllocation(event) {
    const row = event.target.closest("[data-erp--marking-form-target='allocationRow']")
    if (!row) return

    const selected = event.target.checked
    const destroy = row.querySelector("[data-erp--marking-form-target='allocationDestroy']")
    const quantity = row.querySelector("[data-erp--marking-form-target='allocationQuantity']")
    if (destroy) destroy.value = selected ? "0" : "1"
    if (quantity) {
      quantity.disabled = !selected
      if (selected && (parseInt(quantity.value) || 0) === 0) quantity.value = quantity.max || "1"
    }

    this.#syncQuantity()
    this.lookupPrice()
    this.#notifyFormMutation(destroy || quantity)
  }

  selectAllAllocations(event) {
    event.preventDefault()

    this.allocationRowTargets.forEach(row => {
      const checkbox = row.querySelector("[data-erp--marking-form-target='allocationCheckbox']")
      const destroy = row.querySelector("[data-erp--marking-form-target='allocationDestroy']")
      const quantity = row.querySelector("[data-erp--marking-form-target='allocationQuantity']")
      if (checkbox) checkbox.checked = true
      if (destroy) destroy.value = "0"
      if (quantity) {
        quantity.disabled = false
        quantity.value = quantity.max || quantity.value || "0"
      }
    })

    this.#syncQuantity()
    this.lookupPrice()
    const changedField = this.allocationRowTargets
      .map(row => row.querySelector("[data-erp--marking-form-target='allocationDestroy']"))
      .find(Boolean)
    this.#notifyFormMutation(changedField)
  }

  allocationQuantityChanged() {
    this.#syncQuantity()
    this.lookupPrice()
  }

  #notifyFormMutation(field) {
    field?.dispatchEvent(new Event("change", { bubbles: true }))
  }

  // ── Technique and dimensions ─────────────────────────────────

  canonicalTechniqueChanged() {
    if (!this.hasTechniqueTarget || !this.hasCanonicalTechniqueTarget) return

    const selected = this.canonicalTechniqueTarget.selectedOptions[0]
    this.techniqueTarget.value = selected?.dataset.code || ""
    this.techniqueChanged()
  }

  async techniqueChanged() {
    if (this.hasSubTechniqueTarget) this.subTechniqueTarget.value = ""

    this.#refreshBadges()
    this.#syncExtraAvailability()
    this.#refreshCompatibility()
    await this.loadDimensions({ preserveSelection: false })
    this.lookupPrice()
  }

  async subTechniqueChanged() {
    await this.loadDimensions({ preserveSelection: false })
    this.lookupPrice()
  }

  positionChanged() {
    this.#refreshBadges()
    this.#refreshCompatibility()
    this.recalculate()
  }

  zoneChanged() {
    if (!this.hasCanonicalZoneTarget || !this.hasPositionTarget) return

    const selected = this.canonicalZoneTarget.selectedOptions[0]
    if (selected?.value && this.hasCustomZoneTarget) this.customZoneTarget.value = ""
    this.positionTarget.value = selected?.value ? selected.textContent.trim() : ""
    this.positionChanged()
  }

  customZoneChanged() {
    if (!this.hasCustomZoneTarget || !this.hasPositionTarget) return

    const customName = this.customZoneTarget.value.trim()
    if (customName && this.hasCanonicalZoneTarget) this.canonicalZoneTarget.value = ""
    this.positionTarget.value = customName
    this.positionChanged()
  }

  compatibilityOverrideChanged() {
    if (!this.hasCompatibilityReasonTarget) return

    const overridden = this.hasCompatibilityOverrideTarget && this.compatibilityOverrideTarget.checked
    this.compatibilityReasonTarget.classList.toggle("hidden", !overridden)
  }

  compatibilityChanged() {
    this.#refreshCompatibility()
  }

  toggleExtra(event) {
    const row = event.target.closest("[data-erp--marking-form-target='extraRow']")
    const destroy = row?.querySelector("[data-erp--marking-form-target='extraDestroy']")
    if (!destroy) return

    destroy.value = event.target.checked ? "0" : "1"
    this.lookupPrice()
    this.#notifyFormMutation(destroy)
  }

  overrideChanged() {
    if (!this.hasUnitPriceTarget) return

    const overridden = this.#overridden()
    this.unitPriceTarget.readOnly = !overridden
    this.unitPriceTarget.classList.toggle("bg-black/5", !overridden)
    this.#toggleManualPriceReason()

    if (overridden) this.recalculate()
    else this.lookupPrice()
  }

  recalculate() {
    if (this.structuredPricingValue && !this.#overridden()) return this.#markPricingStale()

    this.#refreshSummary()
    this.dispatch("changed")
  }

  async customerContextChanged() {
    await this.loadDimensions({ preserveSelection: true })
    await this.lookupPrice()
  }

  customerTypeValueChanged() {
    if (!this.element.isConnected) return

    this.#syncExtraAvailability()
    this.loadDimensions({ preserveSelection: true })
  }

  async loadDimensions({ preserveSelection = true } = {}) {
    if (!this.hasDimensionTarget || !this.hasTechniqueTarget) return

    const select = this.dimensionTarget
    const previous = select.value
    this._dimensionsAbortController?.abort()

    const technique = this.techniqueTarget.value
    if (!technique || !this.dimensionsUrlValue) {
      select.replaceChildren(new Option("—", ""))
      return
    }
    // On initial load, keep the persisted option in the form until the lookup has
    // succeeded. Otherwise a slow/offline request could make an immediate Save erase it.
    if (!preserveSelection) select.replaceChildren(new Option("—", ""))

    const params = new URLSearchParams({ technique, customer_type: this.#customerType() })
    if (this.hasSubTechniqueTarget && this.subTechniqueTarget.value) {
      params.set("sub_technique", this.subTechniqueTarget.value)
    }
    const request = new AbortController()
    this._dimensionsAbortController = request

    try {
      const response = await fetch(`${this.dimensionsUrlValue}?${params}`, {
        headers: { Accept: "application/json" },
        signal: request.signal
      })
      if (request !== this._dimensionsAbortController) return
      if (!response.ok) return

      const data = await response.json()
      select.replaceChildren(new Option("—", ""))
      data.dimensions.forEach(dimension => select.appendChild(new Option(dimension, dimension)))
      if (preserveSelection && previous) {
        if (!data.dimensions.includes(previous)) select.appendChild(new Option(`${previous} — ${t("unavailable")}`, previous))
        select.value = previous
      }
    } catch (error) {
      if (error.name !== "AbortError") console.warn("[marking-form] Dimensions loading failed:", error)
    }
  }

  // The tariff bracket follows the *total* quantity marked across every attached textile.
  async lookupPrice() {
    if (!this.hasTechniqueTarget || this.#overridden()) return this.recalculate()
    if (this.structuredPricingValue) return this.recalculate()

    const technique = this.techniqueTarget.value
    if (!technique) {
      if (this.hasUnitPriceTarget) this.unitPriceTarget.value = "0.00"
      this.#setFeedback(null)
      return this.recalculate()
    }

    const params = new URLSearchParams({
      technique,
      customer_type: this.#customerType(),
      quantity: this.hasQuantityTarget ? this.quantityTarget.value : "0"
    })
    if (this.hasSubTechniqueTarget && this.subTechniqueTarget.value) params.set("sub_technique", this.subTechniqueTarget.value)
    if (this.hasDimensionTarget && this.dimensionTarget.value) params.set("dimension_1", this.dimensionTarget.value)
    if (this.hasTextileTypeTarget && this.textileTypeTarget.value) params.set("dimension_2", this.textileTypeTarget.value)

    try {
      const response = await fetch(`${this.lookupUrlValue}?${params}`, { headers: { Accept: "application/json" } })
      if (!response.ok) return

      const data = await response.json()
      if (this.hasUnitPriceTarget) this.unitPriceTarget.value = ((data.price_cents || 0) / 100).toFixed(2)
      this.#setFeedback(this.#feedbackFor(data))
      this.recalculate()
    } catch (error) {
      console.warn("[marking-form] Price lookup failed:", error)
    }
  }

  // ── Private ──────────────────────────────────────────────────

  #syncQuantity() {
    const allocationRows = this.allocationRowTargets.filter(row => {
      return row.querySelector("[data-erp--marking-form-target='allocationDestroy']")?.value !== "1"
    })
    const linkRows = this.linkRowTargets.filter(row => {
      return row.querySelector("[data-erp--marking-form-target='linkDestroy']")?.value !== "1"
    })
    const rows = allocationRows.length > 0 ? allocationRows : linkRows
    if (this.hasAllocationCountTarget) this.allocationCountTarget.textContent = allocationRows.length

    if (rows.length === 0) {
      if (this.hasQuantityTarget) {
        this.quantityTarget.readOnly = false
        this.quantityTarget.classList.remove("bg-black/5")
      }
      return this.#refreshSummary()
    }

    const total = rows.reduce((sum, row) => {
      const quantity = row.querySelector("[data-erp--marking-form-target='allocationQuantity']")
        || row.querySelector("[data-erp--marking-form-target='linkQuantity']")
      return sum + (parseInt(quantity?.value) || 0)
    }, 0)

    if (allocationRows.length > 0 && this.hasTextileTypeTarget) {
      const textileTypes = [...new Set(allocationRows.map(row => row.dataset.allocationType).filter(Boolean))]
      this.textileTypeTarget.value = textileTypes.length === 1
        ? (textileTypes[0] === "white" ? "blanc" : "couleur")
        : ""
    }

    if (this.hasQuantityTarget) {
      this.quantityTarget.value = total
      this.quantityTarget.readOnly = true
      this.quantityTarget.classList.add("bg-black/5")
    }

    this.#refreshSummary()
  }

  #refreshSummary() {
    const quantity = this.hasQuantityTarget ? (parseInt(this.quantityTarget.value) || 0) : 0
    const unitPrice = this.hasUnitPriceTarget ? (parseFloat(this.unitPriceTarget.value) || 0) : 0

    if (this.hasQuantitySummaryTarget) this.quantitySummaryTarget.textContent = quantity
    const calculatePrice = !this.structuredPricingValue || this.#overridden()
    if (this.hasUnitPriceSummaryTarget && calculatePrice) {
      this.unitPriceSummaryTarget.textContent = unitPrice > 0 ? this.#currency(unitPrice) : "—"
    }

    const discountType = this.element.querySelector("select[name*='[discount_type]']")?.value || "percentage"
    const discount = parseInt(this.element.querySelector("select[name*='[discount_percentage]']")?.value) || 0
    const fixedDiscount = Math.round((parseFloat(this.element.querySelector("input[name*='[discount_amount]']")?.value) || 0) * 100)
    const complimentary = this.element.querySelector("input[type='checkbox'][name*='[complimentary]']")?.checked
    if (this.hasDiscountSummaryTarget) {
      this.discountSummaryTarget.textContent = discountType === "fixed" && fixedDiscount > 0
        ? `−${this.#currency(fixedDiscount / 100)}`
        : (discount > 0 ? `−${discount} %` : "—")
    }
    if (calculatePrice) {
      const unitCents = Math.round(unitPrice * 100)
      const grossCents = quantity * unitCents
      const discountedTotalCents = discountType === "fixed"
        ? Math.max(0, unitCents - fixedDiscount) * quantity
        : (discount > 0 ? Math.round(grossCents * (100 - discount) / 100) : grossCents)
      const extrasCents = complimentary ? 0 : (parseInt(this.element.dataset.markingExtrasCents) || 0)
      const total = complimentary ? 0 : (discountedTotalCents + extrasCents) / 100
      if (this.hasTotalSummaryTarget) this.totalSummaryTarget.textContent = total > 0 ? this.#currency(total) : "—"
    }

    if (this.hasLinkSummaryTarget) {
      this.linkSummaryTarget.replaceChildren()
      const allocationRows = this.allocationRowTargets.filter(row => {
        return row.querySelector("[data-erp--marking-form-target='allocationDestroy']")?.value !== "1"
      })
      const summaryRows = allocationRows.length > 0 ? allocationRows : this.linkRowTargets
      summaryRows.forEach(row => {
        const destroy = row.querySelector("[data-erp--marking-form-target='allocationDestroy']")
          || row.querySelector("[data-erp--marking-form-target='linkDestroy']")
        if (destroy?.value === "1") return

        const label = row.querySelector("[data-allocation-label]")?.textContent?.trim()
          || row.querySelector("[data-link-label]")?.textContent?.trim()
          || ""
        const quantity = row.querySelector("[data-erp--marking-form-target='allocationQuantity']")
          || row.querySelector("[data-erp--marking-form-target='linkQuantity']")
        const rowQuantity = quantity?.value || "0"

        const chip = document.createElement("span")
        chip.className = "inline-flex items-center gap-1 px-2 py-1 text-xs bg-black/5 border border-black rounded"
        chip.textContent = `${label} · ${rowQuantity}`
        this.linkSummaryTarget.appendChild(chip)
      })
    }
  }

  #refreshBadges() {
    if (this.hasTechniqueBadgeTarget) {
      const option = this.hasCanonicalTechniqueTarget ? this.canonicalTechniqueTarget.selectedOptions[0] : null
      this.techniqueBadgeTarget.textContent = option?.value ? option.textContent : t("untyped")
    }
    if (this.hasPositionBadgeTarget && this.hasPositionTarget) {
      this.positionBadgeTarget.textContent = this.positionTarget.value
    }
  }

  #toggleManualPriceReason() {
    if (!this.hasManualPriceReasonTarget) return

    this.manualPriceReasonTarget.classList.toggle("hidden", !this.#overridden())
  }

  #syncExtraAvailability() {
    const technique = this.hasTechniqueTarget ? this.techniqueTarget.value : ""
    const customerType = this.#customerType()
    let visibleCount = 0

    this.extraRowTargets.forEach(row => {
      const checkbox = row.querySelector("[data-erp--marking-form-target='extraCheckbox']")
      const matches = Boolean(technique)
        && row.dataset.extraTechnique === technique
        && row.dataset.extraCustomerType === customerType
      const selected = checkbox?.checked
      const visible = matches || selected
      row.classList.toggle("hidden", !visible)
      if (checkbox) checkbox.disabled = !visible
      row.querySelector("[data-extra-mismatch]")?.classList.toggle("hidden", matches)
      if (visible) visibleCount += 1
    })

    if (this.hasExtraEmptyTarget) this.extraEmptyTarget.classList.toggle("hidden", visibleCount > 0)
  }

  #refreshCompatibility() {
    const techniqueId = this.hasCanonicalTechniqueTarget ? this.canonicalTechniqueTarget.value : ""
    const zoneId = this.hasCanonicalZoneTarget ? this.canonicalZoneTarget.value : ""
    const customZone = this.hasCustomZoneTarget ? this.customZoneTarget.value.trim() : ""
    const width = this.hasWidthTarget ? parseFloat(this.widthTarget.value) || 0 : 0
    const height = this.hasHeightTarget ? parseFloat(this.heightTarget.value) || 0 : 0

    this.element.querySelectorAll("[data-erp--marking-form-target='compatibilityRow']").forEach(row => {
      let status = "unspecified"
      let details = []
      let rule = null

      if (row.dataset.customerSupplied === "true") {
        status = "approval_required"
        details = [t("compatibility.customer_supplied")]
      } else if (customZone) {
        status = "approval_required"
        details = [t("compatibility.custom_zone")]
      } else if (!techniqueId || !zoneId) {
        details = [t("compatibility.select_definition")]
      } else {
        try {
          rule = JSON.parse(row.dataset.compatibilityRules || "{}")[`${techniqueId}:${zoneId}`]
        } catch {
          rule = null
        }
        status = rule?.status || "unspecified"
        if (!rule) details.push(t("compatibility.unspecified"))
      }

      if (rule) {
        const dimensionsExceeded = (rule.max_width_mm && width > Number(rule.max_width_mm))
          || (rule.max_height_mm && height > Number(rule.max_height_mm))
        if (dimensionsExceeded) {
          status = "incompatible"
          details.push(t("compatibility.dimensions_exceeded"))
        }
        if (rule.max_width_mm || rule.max_height_mm) {
          details.push(t("compatibility.max_dimensions", {
            width: rule.max_width_mm || "—",
            height: rule.max_height_mm || "—"
          }))
        }
        if (rule.inherited) details.push(t("compatibility.inherited"))
        if (rule.operator_note) details.push(rule.operator_note)
      }

      const badge = row.querySelector("[data-compatibility-status]")
      if (badge) {
        const classes = {
          allowed: "border-green-700 bg-green-50 text-green-800",
          approval_required: "border-amber-700 bg-amber-50 text-amber-800",
          incompatible: "border-red-700 bg-red-50 text-red-800",
          unspecified: "border-black/40 bg-erp-card text-black/65"
        }[status]
        badge.className = `rounded border px-2 py-1 text-xs font-medium ${classes}`
        badge.textContent = t(`compatibility.status.${status}`)
      }
      const detail = row.querySelector("[data-compatibility-detail]")
      if (detail) detail.textContent = details.join(" · ")
    })
  }

  #markPricingStale() {
    this.element.dataset.pricingStale = "true"
    this.element.querySelectorAll("[data-pricing-status-badge]").forEach(badge => {
      badge.textContent = t("recalculate_required")
      badge.className = "inline-flex items-center rounded border border-amber-700 bg-amber-50 px-2 py-1 text-xs font-medium text-amber-800"
    })
    if (this.hasUnitPriceSummaryTarget) this.unitPriceSummaryTarget.textContent = "—"
    if (this.hasTotalSummaryTarget) this.totalSummaryTarget.textContent = "—"
    this.element.querySelector("[data-pricing-stale]")?.classList.remove("hidden")
    this.element.querySelector("[data-pricing-breakdown]")?.classList.add("opacity-50")
    this.dispatch("changed")
  }

  #feedbackFor(data) {
    if (data.price_cents != null && !data.quote_required) return null

    switch (data.reason) {
      case "below_minimum":
        return { level: "warning", message: t("below_minimum", { count: data.minimum_quantity }) }
      case "above_maximum":
        return { level: "warning", message: t("above_maximum", { count: data.maximum_quantity }) }
      case "missing_dimensions":
        return { level: "info", message: t("missing_dimensions") }
      case "no_grid":
        return { level: "info", message: t("no_grid") }
      default:
        return data.quote_required ? { level: "warning", message: t("quote_required") } : null
    }
  }

  #setFeedback(feedback) {
    if (!this.hasPriceFeedbackTarget) return

    const element = this.priceFeedbackTarget
    if (!feedback) {
      element.textContent = ""
      element.classList.add("hidden")
      return
    }

    element.textContent = feedback.message
    element.className = `mt-1 text-xs ${feedback.level === "warning" ? "text-amber-700" : "text-black/60"}`
  }

  #overridden() {
    return this.hasPriceOverrideTarget && this.priceOverrideTarget.checked
  }

  #customerType() {
    return this.customerTypeValue || "direct"
  }

  #currency(amount) {
    return `${amount.toFixed(2).replace(".", ",")} €`
  }
}
